import React, { Fragment } from 'react';
import { Button, Grid } from '@mui/material';
import { Add } from '@mui/icons-material';
import { Category as CategoryComponent } from '@Components/Category/Category.component';
import { setLoFConfig } from '@Actions';
import { useDispatch } from 'react-redux';
import { Category } from '@Types/category.types';
import { Component, ComponentProps } from '@Types/component.types';
import { useGlobalization } from '@Translations/useGlobalization';
import { useIsRiskTemplatesEnabled } from '@Hooks/useIsRiskTemplatesEnabled';

interface RenderedCategoriesProps extends ComponentProps {
	categories: Category[];
	totalWeight: number;
	isLoading: boolean;
	handleAddCategoryClicked: () => void;
}

export const RenderedCategories: React.FC<RenderedCategoriesProps> = ({
	categories,
	totalWeight,
	isLoading,
	onAddComponent,
	onEditComponent,
	handleAddCategoryClicked,
}) => {
	const dispatch = useDispatch();
	const { t } = useGlobalization();
	const isRiskTemplatesEnabled = useIsRiskTemplatesEnabled();

	const onCategoryWeightingChanged = (index: number) => (value: number) => {
		dispatch(
			setLoFConfig(
				categories.map((onChangeCat, onChangeIndex) =>
					onChangeIndex === index
						? {
								...onChangeCat,
								weighting: value,
						  }
						: onChangeCat,
				),
			),
		);
	};

	const addComponentToLoF =
		(forInspection: boolean) =>
		(forLoF: boolean, targetCat: Category | undefined): void => {
			onAddComponent(true, forInspection, targetCat);
		};

	const editComponentForLoF =
		(forInspection: boolean) =>
		(
			forLoF: boolean,
			targetCat: Category | undefined,
			component: Component,
		): void => {
			onEditComponent(true, forInspection, targetCat, component);
		};

	if (isLoading) {
		return (
			<Grid item xs={12} style={{ paddingBottom: '12px' }}>
				<CategoryComponent
					isLoading={true}
					forLoF={true}
					LoFCategoryIndex={0}
					isWeightingInvalid={false}
				/>
			</Grid>
		);
	}

	if (isRiskTemplatesEnabled) {
		return (
			<>
				{categories.map((cat, index) => (
					<Grid
						item
						xs={12}
						key={`${cat.name}-${index}`}
						style={{ paddingBottom: '12px' }}>
						<CategoryComponent
							isLoading={false}
							forLoF={true}
							LoFCategoryIndex={index}
							isWeightingInvalid={totalWeight != 100}
							onCategoryWeightingChanged={onCategoryWeightingChanged(
								index,
							)}
							onAddComponent={addComponentToLoF(cat.isCondition)}
							onEditComponent={editComponentForLoF(
								cat.isCondition,
							)}
						/>
					</Grid>
				))}
				<Grid
					item
					xs={12}
					container
					justifyContent="flex-start"
					sx={{ pb: 5 }}>
					<Button
						style={{ marginTop: '4px' }}
						variant="outlined"
						color="primary"
						startIcon={<Add />}
						onClick={handleAddCategoryClicked}>
						{t('Add Category')}
					</Button>
				</Grid>
			</>
		);
	}

	return (
		<>
			{categories.map((cat, index) => (
				<Fragment key={`${cat.name}-${index}`}>
					<Grid item xs={12} style={{ paddingBottom: '12px' }}>
						<CategoryComponent
							isLoading={false}
							forLoF={true}
							LoFCategoryIndex={index}
							isWeightingInvalid={totalWeight != 100}
							onCategoryWeightingChanged={onCategoryWeightingChanged(
								index,
							)}
							onAddComponent={addComponentToLoF(cat.isCondition)}
							onEditComponent={editComponentForLoF(
								cat.isCondition,
							)}
						/>
					</Grid>
					{index === 0 && (
						<Grid item xs={12} container justifyContent="flex-end">
							<Button
								style={{ margin: '12px 0px' }}
								variant="contained"
								color="primary"
								onClick={handleAddCategoryClicked}>
								{t('Add Category')}
							</Button>
						</Grid>
					)}
				</Fragment>
			))}
		</>
	);
};
